import React, { useState, useEffect } from 'react';
import { userSettingsAPI } from '../../services/api';
import './SettingsPage.css';

function NotificationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [settings, setSettings] = useState({
    notificationEmail: true,
    emailClubWall: true,
    emailClubBets: true,
    emailClubBetPeriods: true,
    emailClubReports: true,
    emailClubReminders: true,
    newsletter: true,
    notificationSms: true,
    notificationPush: true,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await userSettingsAPI.getNotificationSettings();
      const apiSettings = response.settings || response;

      // Map snake_case API response to camelCase component state
      setSettings({
        notificationEmail: apiSettings.notification_email ?? true,
        emailClubWall: apiSettings.email_club_wall ?? true,
        emailClubBets: apiSettings.email_club_bets ?? true,
        emailClubBetPeriods: apiSettings.email_club_bet_periods ?? true,
        emailClubReports: apiSettings.email_club_reports ?? true,
        emailClubReminders: apiSettings.email_club_reminders ?? true,
        newsletter: apiSettings.newsletter ?? true,
        notificationSms: apiSettings.notification_sms ?? true,
        notificationPush: apiSettings.notification_push ?? true,
      });
    } catch (error) {
      console.error('Failed to load notification settings:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load notification settings',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (setting) => {
    setSettings((prev) => ({ ...prev, [setting]: !prev[setting] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Convert camelCase to snake_case for API
      const apiData = {
        notification_email: settings.notificationEmail,
        email_club_wall: settings.emailClubWall,
        email_club_bets: settings.emailClubBets,
        email_club_bet_periods: settings.emailClubBetPeriods,
        email_club_reports: settings.emailClubReports,
        email_club_reminders: settings.emailClubReminders,
        newsletter: settings.newsletter,
        notification_sms: settings.notificationSms,
        notification_push: settings.notificationPush,
      };

      await userSettingsAPI.updateNotificationSettings(apiData);
      setMessage({
        type: 'success',
        text: 'Notification settings updated successfully!',
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update notification settings',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="settings-page-content">
      <h2>Notification Settings</h2>
      <p className="page-description">
        Choose how you want to receive notifications and updates.
      </p>

      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      <form onSubmit={handleSubmit} className="settings-form">
        <div className="form-section">
          <h3>Email Notifications</h3>
          <div className="checkbox-group">
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="notificationEmail"
                checked={settings.notificationEmail}
                onChange={() => handleToggle('notificationEmail')}
              />
              <label htmlFor="notificationEmail">
                Enable email notifications
              </label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="emailClubWall"
                checked={settings.emailClubWall}
                onChange={() => handleToggle('emailClubWall')}
              />
              <label htmlFor="emailClubWall">
                Club wall posts and updates
              </label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="emailClubBets"
                checked={settings.emailClubBets}
                onChange={() => handleToggle('emailClubBets')}
              />
              <label htmlFor="emailClubBets">
                Club member bet placements
              </label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="emailClubBetPeriods"
                checked={settings.emailClubBetPeriods}
                onChange={() => handleToggle('emailClubBetPeriods')}
              />
              <label htmlFor="emailClubBetPeriods">
                Bet period start and end notifications
              </label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="emailClubReports"
                checked={settings.emailClubReports}
                onChange={() => handleToggle('emailClubReports')}
              />
              <label htmlFor="emailClubReports">
                Weekly/monthly club reports
              </label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="emailClubReminders"
                checked={settings.emailClubReminders}
                onChange={() => handleToggle('emailClubReminders')}
              />
              <label htmlFor="emailClubReminders">
                Club reminders and important notices
              </label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="newsletter"
                checked={settings.newsletter}
                onChange={() => handleToggle('newsletter')}
              />
              <label htmlFor="newsletter">
                Newsletter and promotional emails
              </label>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>SMS Notifications</h3>
          <div className="checkbox-group">
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="notificationSms"
                checked={settings.notificationSms}
                onChange={() => handleToggle('notificationSms')}
              />
              <label htmlFor="notificationSms">
                Enable SMS notifications
              </label>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Push Notifications</h3>
          <div className="checkbox-group">
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="notificationPush"
                checked={settings.notificationPush}
                onChange={() => handleToggle('notificationPush')}
              />
              <label htmlFor="notificationPush">
                Enable push notifications (browser/mobile)
              </label>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default NotificationSettings;
